import React, { useContext, useEffect, useRef, useState } from 'react';

import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import Button from '@material-ui/core/Button'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import Grid from '@material-ui/core/Grid'
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import SendIcon from '@material-ui/icons/Send';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import Typography from '@material-ui/core/Typography'

import AccommodationExpenses from './AccommodationExpenses';
import AssessmentServices from './AssessmentServices';
import ReferralView from './ReferralViewPanel';
import SubsistenceExpenses from './SubsistenceExpenses';
import TravelExpenses from './TravelExpenses';
// import Typography from '@material-ui/core/Typography'
import QuoteDocumentPreview from './pdf/QuoteDocumentPreview';
import TextField from '@material-ui/core/TextField'
import { checkEmailAddress } from '../../../app/data/validate';
import { navigate } from '@reach/router';
import BookingFormPreview from './pdf/BookingFormPreview';
import CourtDetails from './CourtDetails';
import Spacer from '../../../app/common/ui/Spacer';
import DownloadBookingForm from './docx/BookingFormDoc';
import ConfirmActionDialog from '../_common/ConfirmActionDialog';
import CheckboxSelect from './CheckboxSelect';
import { DbxContext } from '../../../services/DbxContext';


const emailFieldStyle = {
    minWidth: '100%'
}

// const parseEmail = text => text ? text.split(',').map(item => item.trim) : [];

const QuoteSubmitConfirm = ({ onSubmit, onCancel, to = "vicky.referrer@theintermediarycooperative.co.uk", cc = "", intermediary, transaction, digest }) => {
    const cache = useContext(DbxContext);

    const emailToRef = useRef();
    const emailCcRef = useRef();
    const messageRef = useRef();

    const [problem, setProblem] = useState();
    const [mail, setMail] = useState({ to, cc, message: '' });
    const [success, setSuccess] = useState(false);
    const [docs, setDocs] = useState(new Map([['Quote', true], ['Booking Form', false]]));
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        const unsubscribe = cache.subscribe({
            topic: transaction,
            hook: value => {
                setSuccess(value);
            }
        });

        return unsubscribe;
    }, [cache, transaction]);

    const handleChange = field => {
        setMail({ ...mail, ...field });

        if (problem &&
            checkEmailAddress(emailToRef.current.value) &&
            (!emailCcRef.current.value || checkEmailAddress(emailCcRef.current.value))) {
            setProblem(undefined);
        }
    }

    const handleConfirmSend = () => {
        if (!checkEmailAddress(mail.to)) return setProblem(`Please check the To email address - it doesn't look valid`);
        if (mail.cc && !checkEmailAddress(mail.cc)) return setProblem(`Please check the CC email address - it doesn't look quite right`);

        setBusy(true);

        onSubmit({
            email: {
                ...mail, to: mail.to.trim(), cc: mail.cc?.trim(), bcc: intermediary?.email?.trim() ?? ''
            },
            docs
        });

    }

    if (!digest?.court?.name) {
        return <Alert severity="warning" variant="filled" onClose={onCancel}>Please add the court information. If you don't have it at this point then just type 'unknown' for the court name</Alert>
    }

    return (
        <div>
            <Alert severity="info" variant="outlined">Once you click the confirm button a link to the quote will be sent to the referrer and any documents that you select below will be attached so please ensure you have checked them!</Alert>
            <div >
                <Spacer />
                <Typography variant="h6" color="textPrimary">Tick the documents you want to send as attachments to the email</Typography>
                <CheckboxSelect options={Array.from(docs.keys())} values={Array.from(docs.values())} onChange={setDocs} />
                <Spacer />
            </div>
            <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                <Typography variant="h6" color="textPrimary">Please check the email details</Typography>
            </div>
            <Grid container spacing={2} >
                <Grid item xs={6}>
                    <TextField
                        inputRef={emailToRef}
                        onChange={() => handleChange({ to: emailToRef.current.value })}
                        label="To"
                        defaultValue={mail?.to}
                        placeholder="e.g. maria.bloggs@somewhere.com"
                        helperText="Referrer email address"
                        variant="outlined"
                        style={emailFieldStyle}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        inputRef={emailCcRef}
                        onChange={() => handleChange({ cc: emailCcRef.current.value })}
                        label="CC"
                        defaultValue={mail?.cc}
                        placeholder="e.g. joe.bloggs@elsewhere.com"
                        helperText="Court / HMCTS Contact email address"
                        variant="outlined"
                        style={emailFieldStyle}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        inputRef={messageRef}
                        onChange={() => handleChange({ message: messageRef.current.value })}
                        label="Optional Message"
                        placeholder="TiCiT will generate a standard message for the quote. You can include additional text here if you would like to"
                        variant="outlined"
                        multiline
                        rows={10}
                        style={{ minWidth: '100%' }}
                    />
                </Grid>
                <Grid item>
                    {
                        problem && <Alert severity="error" variant="filled">{problem}</Alert>
                    }
                    {
                        !problem && !success &&
                        <Button variant="contained" color="secondary" startIcon={<SendIcon />} disabled={busy}
                            onClick={handleConfirmSend}>
                            Confirm Send
                        </Button>
                    }
                    {
                        success && <Alert severity="success" variant="filled" onClose={() => navigate("/")}><AlertTitle>Success!</AlertTitle>Your quote has been submitted. The required documents will be mailed as attachments to sent to the referrer. Close this message to return to the home page</Alert>
                    }

                </Grid>
            </Grid>
        </div>
    );
}

const ReferralQuoteContent = ({
    courtCase,
    referral,
    notes,
    request,
    quote,
    existingQuote,
    tariff,
    intermediary,
    court,
    onCourtChange,
    onChange,
    onCaseNumberChange,
    onQuoteSubmit,
    onConfirmBooking,
    onDeleteDraft,
    transaction
}) => {
    // quote in this context is the ServiceInvoice object - existingQuote has all the metadata like quotePath, submitted state etc
    const pages = {
        quote: 1,
        preview: 2,
        confirm: 3,
        booking: 4,
        docx: 5,
    }

    const [page, setPage] = useState(pages.quote);
    const [confirmVisible, setConfirmVisible] = useState(false);

    if (!referral || !request || !quote) return <></>;

    const handleDownload = () => {
        DownloadBookingForm({ intermediary, referral, request, courtCase, quote, notes, court, existingQuote });
    }

    const handleBookingConfirm = () => {
        setConfirmVisible(false);
        onConfirmBooking();
    }

    return (
        <div>
            <ConfirmActionDialog
                onConfirm={handleBookingConfirm}
                onCancel={() => setConfirmVisible(false)}
                visible={confirmVisible}
                title="Confirm Booking"
                message="You can confirm this booking yourself if the referrer is unable to. You should only do this if you have a signed booking form. Once you click OK the booking will be created and you should see the quote has moved into the bookings column on your dashboard page"
            />
            <Grid container spacing={1}>
                <Grid item >
                    {/* {page === pages.quote && <Typography variant="h5" color="textSecondary">Referral Quotation</Typography>} */}
                    {page === pages.preview && <Button variant="contained" color="secondary" startIcon={<MailOutlineIcon />}
                        onClick={() => setPage(pages.confirm)}>Send Quote Docs to referrer</Button>}
                    {/* {page === pages.preview && existingQuote?.status === 'submitted' && false &&
                        <Alert variant="outlined" severity="info">If you need to resend this quote, please download the PDF and email the file directly to the referrer as an attachment</Alert>
                    } */}
                </Grid>
                <Grid item >
                    {
                        page !== pages.quote &&
                        <Button variant="outlined" color="primary" startIcon={<KeyboardBackspaceIcon />}
                            onClick={() => setPage(pages.quote)}>Back to Quote</Button>
                    }
                    {
                        page === pages.quote &&
                        <Button variant="outlined" color="primary" startIcon={<PictureAsPdfIcon />} style={{ marginLeft: '0.5rem' }}
                            onClick={() => setPage(pages.preview)}>Quotation PDF</Button>
                    }
                    {
                        page === pages.quote &&
                        <Button variant="outlined" color="primary" startIcon={<PictureAsPdfIcon />} style={{ marginLeft: '0.5rem' }}
                            onClick={() => setPage(pages.booking)}>Booking Form PDF</Button>
                    }
                    {
                        page === pages.quote &&
                        <Button variant="outlined" color="primary" startIcon={<CloudDownloadIcon />} style={{ marginLeft: '0.5rem' }}
                            onClick={handleDownload}>Booking Form Word DOC</Button>
                    }
                    {
                        (page === pages.quote && existingQuote?.status === 'submitted') &&
                        <Button variant="contained" color="primary" startIcon={<MailOutlineIcon />} style={{ marginLeft: '0.5rem' }}
                            onClick={() => setPage(pages.confirm)}>Send Docs to Referrer</Button>
                    }
                    {
                        (page === pages.quote && existingQuote?.status === 'submitted') &&
                        <Button variant="contained" color="secondary" startIcon={<CheckCircleOutlineIcon />} style={{ marginLeft: '0.5rem' }}
                            onClick={() => setConfirmVisible(true)}>Confirm booking</Button>
                    }
                </Grid>
            </Grid>
            {
                page === pages.quote &&
                <div style={{ marginTop: '1rem' }}>
                    <ReferralView referral={referral} request={request} notes={notes} courtCase={courtCase} />
                    <Spacer />
                    <CourtDetails court={court} caseNumber={courtCase?.courtCaseNumber} onChange={onCourtChange} onCaseNumberChange={onCaseNumberChange} label="Court Name" />
                    <AssessmentServices quote={quote} tariff={tariff} onChange={onChange} />
                    <TravelExpenses tariff={tariff} quote={quote} onChange={onChange} />
                    <AccommodationExpenses tariff={tariff} quote={quote} onChange={onChange} />
                    <SubsistenceExpenses tariff={tariff} quote={quote} onChange={onChange} />
                    {
                        existingQuote?.status === 'draft' &&
                        <Button
                            onClick={onDeleteDraft}
                            variant="contained" color="secondary" style={{ marginTop: '1rem' }}>
                            Delete this draft quote
                        </Button>
                    }
                </div>
            }
            {
                page === pages.preview &&
                <div style={{ marginTop: '1rem' }}>
                    <QuoteDocumentPreview intermediary={intermediary} referral={referral} request={request} quote={quote} courtCase={courtCase} notes={notes} />
                </div>
            }
            {
                page === pages.booking &&
                <div style={{ marginTop: '1rem' }}>
                    <BookingFormPreview intermediary={intermediary} referral={referral} request={request} quote={quote} courtCase={courtCase} notes={notes} court={court} />
                </div>
            }
            {
                page === pages.confirm &&
                <div style={{ marginTop: '1rem' }}>
                    <QuoteSubmitConfirm onSubmit={onQuoteSubmit} onCancel={() => setPage(pages.quote)} to={referral.referrer.email} intermediary={intermediary} existingQuote={existingQuote} transaction={transaction} digest={{ intermediary, referral, request, courtCase, quote, notes, court }} />
                </div>
            }
        </div>
    );
};

export default ReferralQuoteContent;